import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import tw, { styled } from 'twin.macro';
import Cookies from 'universal-cookie';

import { TestStatus } from '../../entities/test';
import { useAuth } from '../../hooks/use-auth';
import * as usersService from '../../services/api/users';
import * as countriesService from '../../services/api/countries';
import * as sitesService from '../../services/api/sites';
import { Allergist } from '../../entities/form/allergist';
import { Country } from '../../services/api/countries';
import { Site } from '../../entities/site';

interface FilterState {
  status?: string;
  administratorId?: number | string;
  productId?: number;
  page?: number;
  textSearch?: string;
  siteId?: number;
  countryId?: number;
  questionnaireFilled?: string;
  isPartnerTest?: string;
  addons?: string;
}

interface ComponentProps {
  filterState: FilterState;
  onUpdate: (filter: FilterState, rememberFilter?: boolean) => void;
}

const cookies = new Cookies();
const FILTER_COOKIE_NAME = 'admin_tests_filter_preferences';

const StyledLabel = styled.label`
  ${tw`inline-block text-sm text-gray-500 mb-1`};
`;

export default function TestsFilter({ filterState, onUpdate }: ComponentProps) {
  const { token, user } = useAuth();
  const [allergists, setAllergists] = useState<Allergist[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [sites, setSites] = useState<Site[]>([]);

  const getSavedRememberFilter = (): boolean => {
    const savedPreferences = cookies.get(FILTER_COOKIE_NAME);

    return savedPreferences ? savedPreferences.rememberFilter : false;
  };

  const convertApiAdministratorIdToUi = (administratorId: number | string | undefined): string => {
    if (administratorId === undefined || administratorId === null) {
      return 'all';
    }

    if (administratorId === 'unassigned') {
      return 'unassigned';
    }

    if (typeof administratorId === 'string') {
      if (administratorId.startsWith('unassigned-or-mine')) {
        return 'unassigned-or-mine';
      }

      if (administratorId === 'unassigned-or-mine') {
        return 'unassigned-or-mine';
      }
    }

    if (typeof administratorId === 'number') {
      if (administratorId === user?.id) {
        return 'me';
      }

      return administratorId.toString();
    }

    if (!Number.isNaN(Number(administratorId))) {
      const numId = parseInt(administratorId, 10);

      if (numId === user?.id) {
        return 'me';
      }

      return administratorId;
    }

    return 'all';
  };

  const initialState = {
    textSearch: (filterState.textSearch as string) || '',
    status: filterState.status !== undefined ? (filterState.status as string) : TestStatus.BloodworkDone,
    administratorId: convertApiAdministratorIdToUi(filterState.administratorId as number | string | undefined),
    siteId: filterState.siteId !== undefined ? (filterState.siteId as number).toString() : '',
    countryId: filterState.countryId !== undefined ? (filterState.countryId as number).toString() : '',
    questionnaireFilled:
      filterState.questionnaireFilled !== undefined ? (filterState.questionnaireFilled as string) : 'all',
    isPartnerTest: filterState.isPartnerTest !== undefined ? (filterState.isPartnerTest as string) : 'inhouse',
    addons: filterState.addons !== undefined ? (filterState.addons as string) : 'all',
  };

  const [rememberFilter, setRememberFilter] = useState<boolean>(getSavedRememberFilter());

  const emptyState = {
    textSearch: '',
    status: TestStatus.BloodworkDone,
    administratorId: 'all',
    siteId: '',
    countryId: '',
    questionnaireFilled: 'all',
    isPartnerTest: 'inhouse',
    addons: 'all',
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: TestStatus.Pending, label: 'Pending' },
    { value: TestStatus.BloodRegistered, label: 'Blood Registered' },
    { value: TestStatus.BloodworkDone, label: 'Bloodwork Done' },
    { value: TestStatus.Completed, label: 'Completed' },
    { value: TestStatus.Cancelled, label: 'Cancelled' },
  ];

  const siteOptions = [
    { value: '', label: 'All Sites' },
    ...sites.map((site) => ({
      value: site.id.toString(),
      label: site.domain,
    })),
  ];

  const countryOptions = [
    { value: '', label: 'All Countries' },
    ...countries.map((country) => ({
      value: country.id.toString(),
      label: country.code,
    })),
  ];

  const questionnaireOptions = [
    { value: 'all', label: 'All' },
    { value: 'filled', label: 'Filled' },
    { value: 'missing', label: 'Missing' },
  ];

  const testTypeOptions = [
    { value: 'all', label: 'All Tests' },
    { value: 'inhouse', label: 'In-house' },
    { value: 'partner', label: 'Partner' },
  ];

  const addonsOptions = [
    { value: 'all', label: 'All' },
    { value: 'interpretation', label: 'Interpretation' },
    { value: 'consultation', label: 'Consultation' },
    { value: 'both', label: 'Both' },
    { value: 'none', label: 'None' },
  ];

  const [filter, setFilter] = useState<{
    textSearch: string;
    status: string;
    administratorId: string;
    siteId: string;
    countryId: string;
    questionnaireFilled: string;
    isPartnerTest: string;
    addons: string;
  }>(initialState);

  useEffect(() => {
    const fetchAllergists = async () => {
      try {
        const data = await usersService.getAdministratorsList(token, { showInAdmin: 1 });

        setAllergists(data || []);
      } catch (e) {
        // Failed to fetch allergists
      }
    };

    const fetchCountries = async () => {
      try {
        const data = await countriesService.list(token);

        setCountries(data || []);
      } catch (e) {
        // Failed to fetch countries
      }
    };

    const fetchSites = async () => {
      try {
        const data = await sitesService.list(token);

        setSites(data || []);
      } catch (e) {
        // Failed to fetch sites
      }
    };

    fetchAllergists();
    fetchCountries();
    fetchSites();
  }, [token]);

  const administratorOptions = [
    { value: 'all', label: 'All' },
    { value: 'unassigned-or-mine', label: 'Unassigned or Mine' },
    { value: 'me', label: 'Mine' },
    { value: 'unassigned', label: 'Unassigned' },
    ...allergists.map((allergist) => ({
      value: allergist.id.toString(),
      label: `${allergist.firstName} ${allergist.lastName}`,
    })),
  ];

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildSubmitFilter = (currentFilter: typeof filter) => ({
    ...currentFilter,
    status: currentFilter.status === 'all' ? undefined : currentFilter.status,
    administratorId: currentFilter.administratorId === 'all' ? undefined : currentFilter.administratorId,
    siteId: currentFilter.siteId === '' ? undefined : parseInt(currentFilter.siteId, 10),
    countryId: currentFilter.countryId === '' ? undefined : parseInt(currentFilter.countryId, 10),
    questionnaireFilled: currentFilter.questionnaireFilled === 'all' ? undefined : currentFilter.questionnaireFilled,
    isPartnerTest: currentFilter.isPartnerTest === 'all' ? undefined : currentFilter.isPartnerTest,
    addons: currentFilter.addons === 'all' ? undefined : currentFilter.addons,
  });

  const onTextSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const newFilter = { ...filter, textSearch: value };

    setFilter(newFilter);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onUpdate(buildSubmitFilter(newFilter));
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const onStatusChange = (event: { value: string } | null) => {
    if (!event) return;
    const newFilter = { ...filter, status: event.value };

    setFilter(newFilter);
    onUpdate(buildSubmitFilter(newFilter));
  };

  const onAdministratorChange = (event: { value: string } | null) => {
    if (!event) return;
    const newFilter = { ...filter, administratorId: event.value };

    setFilter(newFilter);
    onUpdate(buildSubmitFilter(newFilter));
  };

  const onSiteChange = (event: { value: string } | null) => {
    if (!event) return;
    const newFilter = { ...filter, siteId: event.value };

    setFilter(newFilter);
    onUpdate(buildSubmitFilter(newFilter));
  };

  const onCountryChange = (event: { value: string } | null) => {
    if (!event) return;
    const newFilter = { ...filter, countryId: event.value };

    setFilter(newFilter);
    onUpdate(buildSubmitFilter(newFilter));
  };

  const onQuestionnaireChange = (event: { value: string } | null) => {
    if (!event) return;
    const newFilter = { ...filter, questionnaireFilled: event.value };

    setFilter(newFilter);
    onUpdate(buildSubmitFilter(newFilter));
  };

  const onTestTypeChange = (event: { value: string } | null) => {
    if (!event) return;
    const newFilter = { ...filter, isPartnerTest: event.value };

    setFilter(newFilter);
    onUpdate(buildSubmitFilter(newFilter));
  };

  const onAddonsChange = (event: { value: string } | null) => {
    if (!event) return;
    const newFilter = { ...filter, addons: event.value };

    setFilter(newFilter);
    onUpdate(buildSubmitFilter(newFilter));
  };

  const onClearFilter = () => {
    setFilter(emptyState);
    onUpdate(buildSubmitFilter(emptyState));
  };

  const onRememberFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    setRememberFilter(checked);
    onUpdate(buildSubmitFilter(filter), checked);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-9">
        <div>
          <StyledLabel htmlFor="search">Search</StyledLabel>
          <input
            type="text"
            id="search"
            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            placeholder="Patient name, Email, Test ID, Order ID..."
            value={filter.textSearch || ''}
            onChange={onTextSearchChange}
          />
        </div>

        <div>
          <StyledLabel htmlFor="status">Status</StyledLabel>
          <Select
            id="status"
            options={statusOptions}
            onChange={onStatusChange}
            value={statusOptions.find((o) => o.value === filter.status)}
          />
        </div>

        <div>
          <StyledLabel htmlFor="administrator">Assigned</StyledLabel>
          <Select
            id="administrator"
            options={administratorOptions}
            onChange={onAdministratorChange}
            value={administratorOptions.find((o) => o.value === filter.administratorId)}
          />
        </div>

        <div>
          <StyledLabel htmlFor="site">Site</StyledLabel>
          <Select
            id="site"
            options={siteOptions}
            onChange={onSiteChange}
            value={siteOptions.find((o) => o.value === filter.siteId)}
          />
        </div>

        <div>
          <StyledLabel htmlFor="country">Country</StyledLabel>
          <Select
            id="country"
            options={countryOptions}
            onChange={onCountryChange}
            value={countryOptions.find((o) => o.value === filter.countryId)}
          />
        </div>

        <div>
          <StyledLabel htmlFor="questionnaire">Questionnaire</StyledLabel>
          <Select
            id="questionnaire"
            options={questionnaireOptions}
            onChange={onQuestionnaireChange}
            value={questionnaireOptions.find((o) => o.value === filter.questionnaireFilled)}
          />
        </div>

        <div>
          <StyledLabel htmlFor="testType">Test Type</StyledLabel>
          <Select
            id="testType"
            options={testTypeOptions}
            onChange={onTestTypeChange}
            value={testTypeOptions.find((o) => o.value === filter.isPartnerTest)}
          />
        </div>

        <div>
          <StyledLabel htmlFor="addons">Addons</StyledLabel>
          <Select
            id="addons"
            options={addonsOptions}
            onChange={onAddonsChange}
            value={addonsOptions.find((o) => o.value === filter.addons)}
          />
        </div>

        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={onClearFilter}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="mt-3 flex items-center">
        <input
          type="checkbox"
          id="rememberFilter"
          checked={rememberFilter}
          onChange={onRememberFilterChange}
          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
        />
        <label htmlFor="rememberFilter" className="ml-2 block text-sm text-gray-700">
          Remember my filter
        </label>
      </div>
    </div>
  );
}
